"use client"

import Image from "next/image"
import { useRef } from "react"

import { useInView } from "@/core/hooks/use-in-view"
import { cn } from "@/core/utils/utils"

/**
 * WhatWeDo section - CSS animations replacing Framer Motion.
 * Uses useInView hook for scroll-triggered animations.
 * Eliminates ~40KB Framer Motion bundle for this component.
 */
export default function WhatWeDo() {
    const headerRef = useRef<HTMLDivElement>(null)
    const cardsRef = useRef<HTMLDivElement>(null)
    const headerInView = useInView(headerRef, { threshold: 0.1 })
    const cardsInView = useInView(cardsRef, { threshold: 0.1 })

    return (
        <section className="py-24 md:py-32 relative overflow-hidden bg-background">
            <div className="container mx-auto px-4">
                <div
                    ref={headerRef}
                    className={cn(
                        "text-center mb-16 md:mb-24 transition-[opacity,transform] duration-700",
                        headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                    )}
                >
                    <span className="text-sm font-medium tracking-widest text-muted-foreground uppercase mb-4 block">
                        What We Do
                    </span>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                        From initial concept <br className="hidden md:block" />
                        to production launch.
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        Delivering complete, intelligent software products with clean architecture, rapid timelines, and zero fluff.
                    </p>
                </div>

                <div
                    ref={cardsRef}
                    className="grid md:grid-cols-3 gap-6 lg:gap-8"
                >
                    {/* Card 1: AI Agents & Automation */}
                    <div
                        className={cn(
                            "group relative rounded-4xl bg-[#1C1C1E] border border-white/10 overflow-hidden h-[500px] md:h-[600px] flex flex-col transition-[opacity,transform] duration-700",
                            cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                        )}
                        style={{ transitionDelay: "0ms" }}
                    >
                        <div className="p-8 md:p-10 relative z-10 text-white">
                            <h3 className="text-3xl font-bold mb-2">AI Agents &amp; <br />Automation</h3>
                            <p className="text-gray-400 mt-4 text-base leading-relaxed">
                                Turn manual work into autonomous systems. Custom AI agents that answer support tickets, extract document data, and automate daily operations.
                            </p>
                        </div>

                        {/* White Nature Visual */}
                        <div className="absolute inset-0 top-[35%]">
                            <div className="relative w-full h-full transform group-hover:scale-105 transition-transform duration-700 ease-out">
                                <Image
                                    src="/assets/home/system-architecture-white.png"
                                    alt="AI Agents and Automation"
                                    fill
                                    className="object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                                    sizes="(max-width: 768px) 100vw, 33vw"
                                />
                                {/* Bottom fade */}
                                <div className="absolute inset-0 bg-linear-to-t from-[#1C1C1E] via-transparent to-transparent" />
                                {/* Top fade for text readability */}
                                <div className="absolute inset-0 bg-linear-to-b from-[#1C1C1E] via-[#1C1C1E]/50 to-transparent h-1/2" />
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Full-Stack Web & Mobile */}
                    <div
                        className={cn(
                            "group relative rounded-4xl bg-[#111] text-white overflow-hidden h-[500px] md:h-[600px] flex flex-col transition-[opacity,transform] duration-700",
                            cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                        )}
                        style={{ transitionDelay: "200ms" }}
                    >
                        <div className="p-8 md:p-10 relative z-10">
                            <h3 className="text-3xl font-bold mb-2">Full-Stack <br />Web &amp; Mobile</h3>
                            <p className="text-gray-400 mt-4 text-base leading-relaxed">
                                Complete application development. High-converting landing pages, SaaS platforms, and mobile apps built with Next.js, React, and cloud backends.
                            </p>
                        </div>

                        {/* White Nature Visual */}
                        <div className="absolute inset-0 top-[35%]">
                            <div className="relative w-full h-full transform group-hover:scale-105 transition-transform duration-700 ease-out">
                                <Image
                                    src="/assets/home/fractional-leadership-white.png"
                                    alt="Full-Stack Web and Mobile Development"
                                    fill
                                    className="object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                                    sizes="(max-width: 768px) 100vw, 33vw"
                                />
                                {/* Bottom fade */}
                                <div className="absolute inset-0 bg-linear-to-t from-[#111] via-transparent to-transparent" />
                                {/* Top fade for text readability */}
                                <div className="absolute inset-0 bg-linear-to-b from-[#111] via-[#111]/50 to-transparent h-1/2" />
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Dedicated Technical Leadership */}
                    <div
                        className={cn(
                            "group relative rounded-4xl bg-[#1C1C1E] border border-white/10 overflow-hidden h-[500px] md:h-[600px] flex flex-col transition-[opacity,transform] duration-700",
                            cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                        )}
                        style={{ transitionDelay: "400ms" }}
                    >
                        <div className="p-8 md:p-10 relative z-10 text-white">
                            <h3 className="text-3xl font-bold mb-2">Dedicated <br />Engineering</h3>
                            <p className="text-gray-400 mt-4 text-base leading-relaxed">
                                Senior hands-on execution for your product. Unblocking critical features, integrating complex APIs, and shipping production releases on time.
                            </p>
                        </div>

                        {/* White Nature Visual */}
                        <div className="absolute inset-0 top-[35%]">
                            <div className="relative w-full h-full transform group-hover:scale-105 transition-transform duration-700 ease-out">
                                <Image
                                    src="/assets/home/high-stakes-execution-white.png"
                                    alt="Dedicated Engineering Leadership"
                                    fill
                                    className="object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                                    sizes="(max-width: 768px) 100vw, 33vw"
                                />
                                {/* Bottom fade */}
                                <div className="absolute inset-0 bg-linear-to-t from-[#1C1C1E] via-transparent to-transparent" />
                                {/* Top fade for text readability */}
                                <div className="absolute inset-0 bg-linear-to-b from-[#1C1C1E] via-[#1C1C1E]/50 to-transparent h-1/2" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    )
}
